import { Box, CardBody, CardHeader, Divider, Grid, HStack, Skeleton, Text, VStack, Button } from "@chakra-ui/react";
import { AlertMsg } from "../../../molecules/AlertMsg";
import { useFetchBusinessPerformances } from "@/hooks/externalApi/google/useFetchBusinessPerformances";
import { useParams } from "react-router-dom";
import { BusinessTitleByRestaurantId } from "../../../organisms/externalApi/google/BusinessTitle";
import { useQueryParamsOfDateRange } from "@/hooks/useQueryParamsOfDateRange";
import { DateRangeFilter } from "../../../molecules/DateRangeFilter";
import { useSaveGoogleReviews } from "@/hooks/externalApi/google/useSaveGoogleReviews";
import { useFetchGoogleRatings } from "@/hooks/externalApi/google/useFetchGoogleRatings";
import { LineGraphCard } from "../../../molecules/card/LineGraphCard";
import { DarkShadowCard } from "../../../molecules/card/DarkShadowCard";
import { useFetchCurrentGoogleRating } from "@/hooks/externalApi/google/useFetchCurrentGoogleRating";
import Rating from '@mui/material/Rating';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import { useFetchNeededRatingCountForNextRating } from "@/hooks/externalApi/google/useFetchNeededRatingCountForNextRating";
import { DonutChart } from "../../../molecules/graph/DonutChart";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton
} from "@chakra-ui/react";
import { useState, useEffect, useCallback, useMemo } from "react";
import { AxiosError } from "axios";
import React from "react";
import ReactApexChart from "react-apexcharts";
import type { ApexOptions } from "apexcharts";

// 問題グラフを個別に停止するためのフラグ
const DISABLE_LINE_CHART = false;   // 平均評価推移
const DISABLE_DONUT_CHART = false;  // ドーナツ

// 共有ユーティリティ: プリミティブ化+有限数化（型安全）
const toStringSafe = (v: unknown): string => {
  try { return String(v); } catch { return ""; }
};

// 文字列数値（カンマや空白、空文字）に強いパーサ
const parseNumberLoose = (v: unknown): number => {
  if (typeof v === "number") return Number.isFinite(v) ? v : 0;
  if (typeof v === "string") {
    const cleaned = v.replace(/,/g, "").trim();
    if (!cleaned) return 0;
    const n = Number(cleaned);
    return Number.isFinite(n) ? n : 0;
  }
  const n = Number(v as any);
  return Number.isFinite(n) ? n : 0;
};

const toFiniteNumber = (v: unknown): number => {
  const n = parseNumberLoose(v);
  return Number.isFinite(n) ? n : 0;
};

// 数値のカンマ区切り整形
const formatNumber = (n: number) => n.toLocaleString();

// Line 用: Record<string, number|unknown> から安全な配列を作る
const buildSafeLineData = (record?: Record<string, unknown>): { labels: string[]; values: number[]; length: number; total: number } => {
  const entries = Object.entries(record ?? {});
  entries.sort(([a], [b]) => (a > b ? 1 : a < b ? -1 : 0));
  const labels: string[] = entries.map(([k]) => toStringSafe(k));
  const values: number[] = entries.map(([, v]) => toFiniteNumber(v));
  const len = Math.min(labels.length, values.length);
  return {
    labels: labels.slice(0, len),
    values: values.slice(0, len),
    total: values.reduce((a, b) => a + (Number.isFinite(b) ? b : 0), 0),
    length: len,
  };
};

// 大量データ対策：時系列の簡易ダウンサンプリング（バケット平均）
const downsampleTimeSeries = (labels: string[], values: number[], maxPoints: number) => {
  if (labels.length <= maxPoints) return { labels, values };
  const n = labels.length;
  const bucketSize = Math.ceil(n / maxPoints);
  const dsLabels: string[] = [];
  const dsValues: number[] = [];
  for (let i = 0; i < n; i += bucketSize) {
    const end = Math.min(i + bucketSize, n);
    // 代表ラベルはバケット末尾（または先頭でもOK）
    dsLabels.push(labels[end - 1]);
    const slice = values.slice(i, end);
    const avg = slice.reduce((a, b) => a + b, 0) / slice.length;
    dsValues.push(Number.isFinite(avg) ? avg : 0);
  }
  return { labels: dsLabels, values: dsValues };
};

// エラーモーダルのプロパティ定義
interface ErrorModalProps {
  isOpen: boolean;
  onClose: () => void;
  errorMessage: string;
  title?: string;
}

// エラーモーダルコンポーネント
const ErrorModal = ({ isOpen, onClose, errorMessage, title = "Error" }: ErrorModalProps) => {
  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>{title}</ModalHeader>
        <ModalCloseButton />
        <ModalBody>{errorMessage}</ModalBody>
        <ModalFooter>
          <button onClick={onClose}>Close</button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

// メインコンポーネント
export const BusinessPerformances = () => {
  const { restaurantId } = useParams();
  const { since, until, requestQueryParams } = useQueryParamsOfDateRange();

  const [isErrorModalOpen, setIsErrorModalOpen] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [errorCount, setErrorCount] = useState(0);

  // handleError を useCallback でラップ
  const handleError = useCallback((error: unknown) => {
    if (errorCount > 0) {
      return;
    }

    if (error instanceof AxiosError && error?.response?.data?.detail) {
      setErrorMsg(error.response.data.detail);
    } else if (error instanceof AxiosError) {
      setErrorMsg("Googleからのレスポンスにエラーが発生しました。");
    } else if (error) {
      setErrorMsg("アカウント連携エラー：Googleアカウントを確認し、BLOCKsに正常に権限が与えられているか確認してください。");
    }

    setIsErrorModalOpen(true);
    setErrorCount(prev => prev + 1);
  }, [errorCount]);

  useEffect(() => {
    if (!isErrorModalOpen) {
      setErrorMsg('');
    }
  }, [isErrorModalOpen]);

  return (
    <>
      <VStack align='start' spacing={5} w='100%'>
        <BusinessTitleByRestaurantId restaurantId={restaurantId} />
        <DateRangeFilter since={since ?? ''} until={until ?? ''} />
        <Ratings restaurantId={restaurantId} requestQueryParams={requestQueryParams} handleError={handleError} />
        <UserViewCount restaurantId={restaurantId} requestQueryParams={requestQueryParams} handleError={handleError} />
        <BusinessPerformanceCards restaurantId={restaurantId} requestQueryParams={requestQueryParams} handleError={handleError} />
      </VStack>
      <ErrorModal
        isOpen={isErrorModalOpen}
        onClose={() => setIsErrorModalOpen(false)}
        errorMessage={errorMsg}
        title="データ取得エラー"
      />
    </>
  );
};

// Ratingsコンポーネント
const Ratings = ({ restaurantId, requestQueryParams, handleError }: {
  restaurantId?: string,
  requestQueryParams: string,
  handleError: (error: any) => void
}) => {
  const { error } = useSaveGoogleReviews(restaurantId);
  useEffect(() => {
    if (error) handleError(error);
  }, [error, handleError]);
  if (error) return null;
  return <RatingContents restaurantId={restaurantId} requestQueryParams={requestQueryParams} />;
};

// RatingContentsコンポーネント
const RatingContents = ({ restaurantId, requestQueryParams }: { restaurantId?: string, requestQueryParams: string }) => {
  const { ratingData, isLoading } = useFetchGoogleRatings(restaurantId, requestQueryParams);

  // 日付ソート＋安全化
  const rawPairs = (ratingData ?? [])
    .map(item => [toStringSafe(item.date), toFiniteNumber(item.rating)] as const)
    .sort(([d1], [d2]) => (d1 > d2 ? 1 : d1 < d2 ? -1 : 0));
  const dates = rawPairs.map(([d]) => d);
  const ratings = rawPairs.map(([, v]) => v);

  const { labels: safeLabels, values: safeValues, length: alignedLen } = useMemo(() => {
    const ls = dates.map(toStringSafe);
    const vs = ratings.map(toFiniteNumber);
    const len = Math.min(ls.length, vs.length);
    return { labels: ls.slice(0, len), values: vs.slice(0, len), length: len };
  }, [dates, ratings]);

  // ダウンサンプリング
  const MAX_POINTS = 180;
  const { labels: dsLabels, values: dsValues } = useMemo(() => {
    return downsampleTimeSeries(safeLabels, safeValues, MAX_POINTS);
  }, [safeLabels, safeValues]);

  // 最終ガード
  const apexLabels = JSON.parse(JSON.stringify(dsLabels)) as string[];
  const apexValues = JSON.parse(JSON.stringify(dsValues)) as number[];
  const lineOk = Array.isArray(apexLabels)
    && Array.isArray(apexValues)
    && apexLabels.length === apexValues.length
    && apexLabels.length > 0
    && apexLabels.every(v => typeof v === 'string')
    && apexValues.every(v => typeof v === 'number' && Number.isFinite(v));

  // 次フレームでのみマウントするゲート
  const shouldRender = !isLoading && lineOk && !DISABLE_LINE_CHART;
  const [renderLine, setRenderLine] = useState(false);
  useEffect(() => {
    let id: ReturnType<typeof setTimeout> | null = null;
    if (shouldRender) {
      id = setTimeout(() => setRenderLine(true), 0);
    } else {
      setRenderLine(false);
    }
    return () => {
      if (id) clearTimeout(id);
      setRenderLine(false);
    };
  }, [shouldRender]);

  const isEmpty = alignedLen === 0;

  return (
    <Box w='100%'>
      <DarkShadowCard rounded='md'>
        <CardHeader p={3}>評価</CardHeader>
        <Divider m='0' borderColor='gray.200' />
        <CardBody p={3}>
          {isEmpty || !lineOk ? (
            <>データが存在しません</>
          ) : (
            <VStack align='start' spacing={3}>
              <VStack align='start' spacing={1}>
                <CurrentRating restaurantId={restaurantId} />
                <NeededRatingCount restaurantId={restaurantId} />
              </VStack>
              {DISABLE_LINE_CHART ? (
                <Text fontSize='sm' color='gray.500'>グラフを一時停止中です。</Text>
              ) : renderLine ? (
                <Box w='100%'>
                  {/* 小数点第一位に丸めた値を使用 */}
                  <LineGraphCard
                    title='平均評価推移'
                    labels={apexLabels}
                    values={apexValues.map(v => Math.round(v * 10) / 10)}
                    height='200'
                  />
                </Box>
              ) : (
                <Skeleton isLoaded={false} rounded='md' w='100%'>
                  <Box height='200px' />
                </Skeleton>
              )}
            </VStack>
          )}
        </CardBody>
      </DarkShadowCard>
    </Box>
  );
};

// CurrentRatingコンポーネント
const CurrentRating = ({ restaurantId }: { restaurantId?: string }) => {
  const theme = createTheme();
  const { currentRating, isLoading } = useFetchCurrentGoogleRating(restaurantId);
  return (
    <Skeleton isLoaded={!isLoading}>
      <HStack>
        <Text m={0} fontSize='lg'>{currentRating}</Text>
        <ThemeProvider theme={theme}>
          <Box as={Rating} fontSize='24px' name="half-rating-read" value={Number(currentRating)} precision={0.1} readOnly />
        </ThemeProvider>
      </HStack>
    </Skeleton>
  );
};

// NeededRatingCountコンポーネント
const NeededRatingCount = ({ restaurantId }: { restaurantId?: string }) => {
  const { neededRatingCountForNextRating, isLoading } = useFetchNeededRatingCountForNextRating(restaurantId);
  return (
    <Skeleton isLoaded={!isLoading}>
      <Text m={0} fontSize='sm' color='gray.500'>
        ★{neededRatingCountForNextRating?.next_higher_rating}をあと{neededRatingCountForNextRating?.needed_rating_count}個で評価アップ！
      </Text>
    </Skeleton>
  );
};

// UserViewCount（ドーナツ）
const UserViewCount = ({ restaurantId, requestQueryParams, handleError }: {
  restaurantId?: string,
  requestQueryParams: string,
  handleError: (error: any) => void
}) => {
  const { businessPerformances, isBusinessPerformanceLoading, error } = useFetchBusinessPerformances(restaurantId, requestQueryParams);

  // フックの順序を守るため、副作用で通知
  useEffect(() => {
    if (error) handleError(error);
  }, [error, handleError]);

  // デバッグ表示トグルとヘルパ
  const [showDebug, setShowDebug] = useState(true);
  const head = (obj: Record<string, any> | undefined, n = 5) => {
    if (!obj) return [];
    const entries = Object.entries(obj);
    return entries.slice(0, n);
  };

  const DEBUG_DONUT_DATA_ONLY = false;

  const labels: string[] = ['Google マップ - モバイル', 'Google 検索 - モバイル', 'Google マップ - PC', 'Google 検索 - PC'];

  // 文字列数値にも強い合計計算
  const calculateTotal = (data: Record<string, number> | undefined) =>
    Object.values(data ?? {}).reduce((acc, value) => acc + parseNumberLoose(value), 0);

  const mapMobileTotal = calculateTotal(businessPerformances?.BUSINESS_IMPRESSIONS_MOBILE_MAPS);
  const searchMobileTotal = calculateTotal(businessPerformances?.BUSINESS_IMPRESSIONS_MOBILE_SEARCH);
  const searchPcTotal = calculateTotal(businessPerformances?.BUSINESS_IMPRESSIONS_DESKTOP_SEARCH);
  const mapPcTotal = calculateTotal(businessPerformances?.BUSINESS_IMPRESSIONS_DESKTOP_MAPS);

  // 正規化＋プレーン化
  const safeSeries = [mapMobileTotal, searchMobileTotal, searchPcTotal, mapPcTotal].map(n => (n < 0 ? 0 : n));
  const apexLabels = JSON.parse(JSON.stringify(labels)) as string[];
  const apexSeries = JSON.parse(JSON.stringify(safeSeries)) as number[];

  // 最終ガード
  const total = apexSeries.reduce((a, b) => a + b, 0);
  const donutOk = Array.isArray(apexLabels)
    && Array.isArray(apexSeries)
    && apexLabels.length === apexSeries.length
    && total > 0
    && apexLabels.every(v => typeof v === 'string')
    && apexSeries.every(v => typeof v === 'number' && Number.isFinite(v));

  const keys = Object.keys(businessPerformances?.BUSINESS_IMPRESSIONS_MOBILE_MAPS ?? {});

  // デバッグ用の要約（画面表示 + console 出力）
  const debugSummary = {
    labels: apexLabels,
    series: apexSeries,
    total,
    okToRender: donutOk && !isBusinessPerformanceLoading,
    rawHead: {
      MOBILE_MAPS: head(businessPerformances?.BUSINESS_IMPRESSIONS_MOBILE_MAPS),
      MOBILE_SEARCH: head(businessPerformances?.BUSINESS_IMPRESSIONS_MOBILE_SEARCH),
      DESKTOP_SEARCH: head(businessPerformances?.BUSINESS_IMPRESSIONS_DESKTOP_SEARCH),
      DESKTOP_MAPS: head(businessPerformances?.BUSINESS_IMPRESSIONS_DESKTOP_MAPS),
    }
  };
  useEffect(() => {
    // eslint-disable-next-line no-console
    console.debug("[Donut Debug] ビジネスプロフィールを閲覧したユーザー数", debugSummary);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(debugSummary)]);

  // 次フレームでのみマウントするゲート
  const shouldRender = !isBusinessPerformanceLoading && donutOk && !DISABLE_DONUT_CHART && !DEBUG_DONUT_DATA_ONLY;
  const [renderDonut, setRenderDonut] = useState(false);
  useEffect(() => {
    let id: ReturnType<typeof setTimeout> | null = null;
    if (shouldRender) {
      id = setTimeout(() => setRenderDonut(true), 0);
    } else {
      setRenderDonut(false);
    }
    return () => {
      if (id) clearTimeout(id);
      setRenderDonut(false);
    };
  }, [shouldRender]);

  // react-apexcharts に直接渡すオプション/シリーズ（安定化）
  const inlineSeries = useMemo(() => apexSeries.map(v => Math.round(v)), [apexSeries]);
  const inlineOptions = useMemo<ApexOptions>(() => ({
    chart: {
      type: "donut",
      animations: { enabled: false },
      toolbar: { show: false },
      id: "user-views-donut",
    },
    labels: apexLabels,
    legend: {
      position: "right",
      horizontalAlign: "left",
      formatter: (seriesName: string, opts) => {
        const idx = opts.seriesIndex;
        const value = inlineSeries[idx] ?? 0;
        const totals = (opts.w.globals.seriesTotals || []) as number[];
        const sum = totals.reduce((a, b) => a + b, 0) || 1;
        const pct = ((value / sum) * 100).toFixed(1);
        return `${seriesName}  ${formatNumber(value)}  (${pct}%)`;
      }
    },
    dataLabels: { enabled: false },
    tooltip: {
      y: { formatter: (val: number) => formatNumber(val) }
    },
    plotOptions: {
      pie: {
        donut: {
          size: "70%",
          labels: {
            show: true,          // 中央ラベルを有効化（重要）
            name: { show: false },
            value: { show: false },
            total: {
              show: true,
              label: "Total",    // 1行目に表示されるテキスト
              formatter: (w) => {
                const totals = (w.globals.seriesTotals || []) as number[];
                const sum = totals.reduce((a, b) => a + b, 0);
                return formatNumber(sum);  // 2行目の数値（カンマ区切り）
              }
            }
          }
        }
      }
    },
    colors: ['#D92F25', '#F19801', '#1B73E9', '#1E8E3C'],
    stroke: { width: 0 },
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [apexLabels, inlineSeries]);

  // ここで表示内容を組み立て（ネスト三項を排除）
  let content: JSX.Element;
  if (error) {
    content = <Text fontSize='sm' color='red.500'>データ取得に失敗しました。</Text>;
  } else if (isBusinessPerformanceLoading) {
    content = (
      <Skeleton isLoaded={false} rounded='md' w='100%'>
        <Box height='220px' />
      </Skeleton>
    );
  } else if (DEBUG_DONUT_DATA_ONLY) {
    content = (
      <Box mt={2}>
        <Text fontSize='sm' color='gray.600' m={0}>Debug summary:</Text>
        <Box mt={2} p={2} bg='gray.50' borderWidth='1px' borderRadius='md' maxW='100%' overflowX='auto'>
          <pre style={{ margin: 0, fontSize: '12px', whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
{JSON.stringify(debugSummary, null, 2)}
          </pre>
        </Box>
        <Button mt={2} size='xs' variant='outline' onClick={() => setShowDebug(v => !v)}>
          {showDebug ? 'rawHead 非表示' : 'rawHead 表示'}
        </Button>
        {showDebug && (
          <Box mt={2} p={2} bg='gray.50' borderWidth='1px' borderRadius='md' maxW='100%' overflowX='auto'>
            <Text fontSize='xs' color='gray.600' m={0}>rawHead:</Text>
            <pre style={{ margin: 0, fontSize: '12px', whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
{JSON.stringify(debugSummary.rawHead, null, 2)}
            </pre>
          </Box>
        )}
      </Box>
    );
  } else if (!donutOk) {
    content = <Text fontSize='sm' color='gray.500'>表示できるデータがありません。</Text>;
  } else if (DISABLE_DONUT_CHART) {
    content = <Text fontSize='sm' color='gray.500'>グラフを一時停止中です。</Text>;
  } else if (renderDonut) {
    const inlineTotal = inlineSeries.reduce((a, b) => a + b, 0);
    content = (
      // 相対配置のコンテナで中央にテキストをオーバーレイ
      <Box w='100%' position='relative'>
        <ReactApexChart
          key={`user-views-donut-${apexLabels.join("|")}-${inlineSeries.join(",")}`}
          options={inlineOptions}
          series={inlineSeries}
          type="donut"
          width="100%"
        />
        {/* 中央の Total 表示（改行して数値） */}
        <Box
          position='absolute'
          top='48%'
          left='25%'
          transform='translate(-50%, -50%)'
          textAlign='center'
          pointerEvents='none'
        >
          <Text m={0} fontSize='sm' color='gray.600' lineHeight='1.1'>Total</Text>
          <Text m={0} fontSize='xl' fontWeight='bold' color='gray.700' lineHeight='1.1'>
            {formatNumber(inlineTotal)}
          </Text>
        </Box>
      </Box>
    );
  } else {
    content = (
      <Skeleton isLoaded={false} rounded='md' w='100%'>
        <Box height='220px' />
      </Skeleton>
    );
  }

  return (
    <DarkShadowCard rounded='md' w={'100%'}>
      <CardHeader p={3}>
        ビジネスプロフィールを閲覧したユーザー数
      </CardHeader>
      <Divider m='0' borderColor='gray.200' />
      <CardBody p={3}>
        <Box w={{ base: '100%', md: '500px' }}>
          <Text m={0} color='gray.500' fontSize='sm'>{keys[0]}~{keys[keys.length - 1]}</Text>
          {content}
        </Box>
      </CardBody>
    </DarkShadowCard>
  );
};

// BusinessPerformanceCardsコンポーネント
const performanceDataGroup1 = [
  { title: "Googleマップインプレッション - PC", dataKey: "BUSINESS_IMPRESSIONS_DESKTOP_MAPS" },
  { title: "Google検索インプレッション - PC", dataKey: "BUSINESS_IMPRESSIONS_DESKTOP_SEARCH" },
  { title: "Googleマップインプレッション - モバイル", dataKey: "BUSINESS_IMPRESSIONS_MOBILE_MAPS" },
  { title: "Google検索インプレッション - モバイル", dataKey: "BUSINESS_IMPRESSIONS_MOBILE_SEARCH" },
];

const performanceDataGroup2 = [
  { title: "ルートリクエスト数", dataKey: "BUSINESS_DIRECTION_REQUESTS" },
  { title: "通話ボタンクリック数", dataKey: "CALL_CLICKS" },
  { title: "ウェブサイトクリック数", dataKey: "WEBSITE_CLICKS" },
  { title: "予約数", dataKey: "BUSINESS_BOOKINGS" },
  { title: "メニューコンテンツクリック数", dataKey: "BUSINESS_FOOD_MENU_CLICKS" },
  { title: "料理の注文数", dataKey: "BUSINESS_FOOD_ORDERS" },
  { title: "受信したメッセージスレッド数", dataKey: "BUSINESS_CONVERSATIONS" },
];

const BusinessPerformanceCards = ({
  restaurantId,
  requestQueryParams,
  handleError
}: {
  restaurantId?: string;
  requestQueryParams: string;
  handleError: (error: any) => void;
}) => {
  const { businessPerformances, isBusinessPerformanceLoading, error } = useFetchBusinessPerformances(
    restaurantId,
    requestQueryParams
  );

  useEffect(() => {
    if (error) {
      handleError(error);
    }
  }, [error, handleError]);

  if (error && (error as any).response?.status === 404) {
    return <AlertMsg msg={(error as any).response.data.message} description={(error as any).response.data.description} />;
  }
  if (!businessPerformances) return null;

  return (
    <Grid templateColumns={{ base: "1fr", md: "repeat(2, 1fr)" }} gap={3} w="100%">
      {performanceDataGroup1.map(({ title, dataKey }) => {
        const record = businessPerformances[dataKey as keyof typeof businessPerformances] as Record<string, unknown> | undefined;
        const { labels, values, length } = buildSafeLineData(record);
        const apexLabels = JSON.parse(JSON.stringify(labels)) as string[];
        const apexValues = JSON.parse(JSON.stringify(values)) as number[];
        const ok =
          length > 0 &&
          Array.isArray(apexLabels) && Array.isArray(apexValues) &&
          apexLabels.length === apexValues.length &&
          apexLabels.every(v => typeof v === 'string') &&
          apexValues.every(v => typeof v === 'number' && Number.isFinite(v));

        const body = ok ? (
          <LineGraphCard title={title} labels={apexLabels} values={apexValues} />
        ) : (
          <Text fontSize='sm' color='gray.500'>{title}: 表示できるデータがありません。</Text>
        );

        return (
          <Skeleton key={title} isLoaded={!isBusinessPerformanceLoading} borderRadius={5}>
            {body}
          </Skeleton>
        );
      })}
      {performanceDataGroup2.map(({ title, dataKey }) => {
        const record = businessPerformances[dataKey as keyof typeof businessPerformances] as Record<string, unknown> | undefined;
        const { labels, values, length } = buildSafeLineData(record);
        const apexLabels = JSON.parse(JSON.stringify(labels)) as string[];
        const apexValues = JSON.parse(JSON.stringify(values)) as number[];
        const ok =
          length > 0 &&
          Array.isArray(apexLabels) && Array.isArray(apexValues) &&
          apexLabels.length === apexValues.length &&
          apexLabels.every(v => typeof v === 'string') &&
          apexValues.every(v => typeof v === 'number' && Number.isFinite(v));

        const body = ok ? (
          <LineGraphCard title={title} labels={apexLabels} values={apexValues} />
        ) : (
          <Text fontSize='sm' color='gray.500'>{title}: 表示できるデータがありません。</Text>
        );

        return (
          <Skeleton key={title} isLoaded={!isBusinessPerformanceLoading} borderRadius={5}>
            {body}
          </Skeleton>
        );
      })}
    </Grid>
  );
};